import { ToolItem } from '../types';

export interface TimeDateToolItem extends ToolItem {
  timeCategory: 'calculators-age' | 'timers-clocks' | 'timezones-meeting' | 'date-business' | 'timestamps-formatting';
  timeCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const TIME_DATE_CATEGORIES_TABS = [
  { id: 'all', label: 'All Time & Date Tools', icon: 'Clock' },
  { id: 'calculators-age', label: 'Age & Duration', icon: 'Calendar' },
  { id: 'timers-clocks', label: 'Timers & Clocks', icon: 'Timer' },
  { id: 'timezones-meeting', label: 'Time Zones & World Clock', icon: 'Globe' },
  { id: 'date-business', label: 'Calendars & Business Days', icon: 'Briefcase' },
  { id: 'timestamps-formatting', label: 'Timestamps & Formats', icon: 'Binary' }
];

export const TIME_DATE_TOOLS_DATA: TimeDateToolItem[] = [
  // --- AGE & DURATION (6 Tools) ---
  {
    id: 'age-calculator',
    name: 'Age Calculator',
    description: 'Calculate your exact age in years, months, weeks, days, hours, and seconds.',
    timeCategory: 'calculators-age',
    timeCategoryLabel: 'Age & Duration',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'UserCheck',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'age-on-any-date',
    name: 'Age on Any Date',
    description: 'Determine your exact age or milestone age on any past or future date.',
    timeCategory: 'calculators-age',
    timeCategoryLabel: 'Age & Duration',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'CalendarHeart',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'birthday-countdown',
    name: 'Birthday Countdown',
    description: 'Track how many days, hours, and minutes remain until your next birthday.',
    timeCategory: 'calculators-age',
    timeCategoryLabel: 'Age & Duration',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Gift',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'date-difference-calculator',
    name: 'Date Difference Calculator',
    description: 'Calculate exact number of days, weeks, months, and years between two dates.',
    timeCategory: 'calculators-age',
    timeCategoryLabel: 'Age & Duration',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'CalendarRange',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'time-difference-calculator',
    name: 'Time Difference Calculator',
    description: 'Find the precise duration and hours difference between two specific times.',
    timeCategory: 'calculators-age',
    timeCategoryLabel: 'Age & Duration',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Clock',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'duration-calculator',
    name: 'Duration Calculator',
    description: 'Add or subtract hours, minutes, and seconds to calculate total activity duration.',
    timeCategory: 'calculators-age',
    timeCategoryLabel: 'Age & Duration',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Hourglass',
    isPopular: false,
    actionType: 'generic'
  },

  // --- TIMERS & CLOCKS (6 Tools) ---
  {
    id: 'countdown-timer',
    name: 'Countdown Timer',
    description: 'Set custom online countdown alarms with audio alerts and fullscreen view.',
    timeCategory: 'timers-clocks',
    timeCategoryLabel: 'Timers & Clocks',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Timer',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'countdown-calculator',
    name: 'Countdown Calculator',
    description: 'Calculate time remaining until upcoming target events, holidays, or deadlines.',
    timeCategory: 'timers-clocks',
    timeCategoryLabel: 'Timers & Clocks',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'CalendarClock',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'stopwatch',
    name: 'Stopwatch',
    description: 'Precision online stopwatch with lap recording, split times, and millisecond accuracy.',
    timeCategory: 'timers-clocks',
    timeCategoryLabel: 'Timers & Clocks',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'Watch',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'pomodoro-timer',
    name: 'Pomodoro Timer',
    description: 'Boost productivity with 25-minute focus sprints and customizable break intervals.',
    timeCategory: 'timers-clocks',
    timeCategoryLabel: 'Timers & Clocks',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Flame',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'digital-clock',
    name: 'Digital Clock',
    description: 'Fullscreen live digital clock with 12h/24h formats, seconds, and date display.',
    timeCategory: 'timers-clocks',
    timeCategoryLabel: 'Timers & Clocks',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Tv',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'world-clock',
    name: 'World Clock',
    description: 'View current local time and day/night status across global major cities simultaneously.',
    timeCategory: 'timers-clocks',
    timeCategoryLabel: 'Timers & Clocks',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Globe',
    isPopular: true,
    actionType: 'generic'
  },

  // --- TIME ZONES & MEETING (2 Tools) ---
  {
    id: 'timezone-converter',
    name: 'Timezone Converter',
    description: 'Easily convert date and time between EST, PST, UTC, GMT, IST, and all world timezones.',
    timeCategory: 'timezones-meeting',
    timeCategoryLabel: 'Time Zones & World Clock',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'ArrowLeftRight',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'time-zone-meeting-planner',
    name: 'Time Zone Meeting Planner',
    description: 'Find overlapping working hours across multiple global time zones for remote teams.',
    timeCategory: 'timezones-meeting',
    timeCategoryLabel: 'Time Zones & World Clock',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Users',
    isPopular: true,
    actionType: 'generic'
  },

  // --- CALENDARS & BUSINESS DAYS (7 Tools) ---
  {
    id: 'calendar',
    name: 'Calendar',
    description: 'Interactive monthly and annual calendar viewer with week numbers and holiday lists.',
    timeCategory: 'date-business',
    timeCategoryLabel: 'Calendars & Business Days',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Calendar',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'calendar-generator',
    name: 'Calendar Generator',
    description: 'Create and print customized monthly or annual calendars for planning.',
    timeCategory: 'date-business',
    timeCategoryLabel: 'Calendars & Business Days',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Printer',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'working-days-calculator',
    name: 'Working Days Calculator',
    description: 'Calculate work days between two dates, excluding weekends and public holidays.',
    timeCategory: 'date-business',
    timeCategoryLabel: 'Calendars & Business Days',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Briefcase',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'business-days-calculator',
    name: 'Business Days Calculator',
    description: 'Add or subtract business days to find future project delivery dates.',
    timeCategory: 'date-business',
    timeCategoryLabel: 'Calendars & Business Days',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Building2',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'leap-year-checker',
    name: 'Leap Year Checker',
    description: 'Check if any given year is a leap year and view future leap year schedules.',
    timeCategory: 'date-business',
    timeCategoryLabel: 'Calendars & Business Days',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'CheckCircle',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'day-of-week-finder',
    name: 'Day of Week Finder',
    description: 'Discover what day of the week (Monday - Sunday) any historical or future date falls on.',
    timeCategory: 'date-business',
    timeCategoryLabel: 'Calendars & Business Days',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Search',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'week-number-calculator',
    name: 'Week Number Calculator',
    description: 'Find current ISO week number or lookup week numbers for any date of the year.',
    timeCategory: 'date-business',
    timeCategoryLabel: 'Calendars & Business Days',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Hash',
    isPopular: false,
    actionType: 'generic'
  },

  // --- TIMESTAMPS & FORMATTING (4 Tools) ---
  {
    id: 'unix-timestamp-converter',
    name: 'Unix Timestamp Converter',
    description: 'Convert Unix epoch timestamps to human-readable dates and vice versa in real time.',
    timeCategory: 'timestamps-formatting',
    timeCategoryLabel: 'Timestamps & Formats',
    iconBgColor: 'bg-slate-500/15',
    iconTextColor: 'text-slate-400',
    iconName: 'Binary',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'timestamp-converter',
    name: 'Timestamp Converter',
    description: 'Convert ISO 8601, UTC, millisecond, and epoch timestamps into standard localized dates.',
    timeCategory: 'timestamps-formatting',
    timeCategoryLabel: 'Timestamps & Formats',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Code',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'date-formatter',
    name: 'Date Formatter',
    description: 'Format dates into YYYY-MM-DD, MM/DD/YYYY, DD-MM-YYYY, or custom locale strings.',
    timeCategory: 'timestamps-formatting',
    timeCategoryLabel: 'Timestamps & Formats',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'FileText',
    isPopular: false,
    actionType: 'generic'
  },
  {
    id: 'time-formatter',
    name: 'Time Formatter',
    description: 'Convert time strings between 12-hour AM/PM, 24-hour military time, and ISO formats.',
    timeCategory: 'timestamps-formatting',
    timeCategoryLabel: 'Timestamps & Formats',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Sparkles',
    isPopular: false,
    actionType: 'generic'
  }
];

export const TIME_DATE_FAQS = [
  {
    question: 'Are all Time and Date calculations performed locally in my browser?',
    answer: 'Yes! All calculations, age calculations, countdowns, stopwatches, and timezone conversions are processed client-side in your web browser. No personal data or dates are ever sent to external servers.'
  },
  {
    question: 'How accurate is the Timezone Converter?',
    answer: 'The Timezone Converter utilizes standard IANA timezone databases built into modern browsers, automatically accounting for Daylight Saving Time (DST) shifts globally.'
  },
  {
    question: 'How do Working Days Calculators account for holidays?',
    answer: 'Our Working Days and Business Days tools allow you to exclude weekends (Saturday & Sunday) as well as customize or toggle national public holidays.'
  },
  {
    question: 'Can I use these timers and stopwatches offline?',
    answer: 'Yes. Once loaded, all clocks, stopwatches, countdown timers, and Pomodoro tools continue functioning perfectly even if your internet connection drops.'
  }
];
